<!-- packages/studio/src/components/army/CategoryEditor.vue -->
<!-- Edits an enhancement category (the top group level, e.g. "Monstrous Traits"
     or "Great Endrinworks"): its name, removal, and the keyword filter that
     decides which units the category is assigned to. Its tables are managed in
     the sidebar (the + next to the category adds a table). -->
<template>
  <div class="category-editor">
    <TitleBar
      :name="name"
      noun="Category"
      hide-duplicate
      @rename="$emit('rename', $event)"
      @delete="$emit('delete')"
    />
    <div class="category-editor__body">
      <p class="category-editor__hint">
        Add tables to this category with the <b>+</b> next to its name in the sidebar — each table
        (e.g. “Monstrous Traits: Kruleboyz”) holds the enhancement options units can choose from.
      </p>

      <section class="category-editor__section">
        <div class="st-eyebrow">Assigned units</div>
        <p class="category-editor__sub">
          Units whose keywords match get this category in their allowed enhancements when the army
          is merged. The restriction text on every table in the category is generated from these.
        </p>
        <StField group label="Required keywords" hint="A unit must have all of these">
          <KeywordsInput
            raw
            uppercase
            suggestion-scope="unit"
            placeholder="e.g. MONSTER"
            :model-value="keywords"
            @update:model-value="emitRequirement({ keywords: $event })"
          />
        </StField>
        <StField group label="Excluded keywords" hint="A unit must have none of these">
          <KeywordsInput
            raw
            uppercase
            suggestion-scope="unit"
            placeholder="e.g. UNIQUE"
            :model-value="nonKeywords"
            @update:model-value="emitRequirement({ nonKeywords: $event })"
          />
        </StField>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { EnhancementRequirement } from '@scrolldex/shared';
import TitleBar from '../editor/TitleBar.vue';
import KeywordsInput from '../KeywordsInput.vue';
import { StField } from '../ui/index.js';

const props = withDefaults(
  defineProps<{
    name: string;
    keywords?: string[];
    nonKeywords?: string[];
  }>(),
  { keywords: () => [], nonKeywords: () => [] },
);
const emit = defineEmits<{
  (e: 'rename', next: string): void;
  (e: 'delete'): void;
  (e: 'update:requirement', value: EnhancementRequirement): void;
}>();

function emitRequirement(part: Partial<EnhancementRequirement>) {
  emit('update:requirement', {
    keywords: props.keywords,
    nonKeywords: props.nonKeywords,
    ...part,
  });
}
</script>

<style scoped>
.category-editor {
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.category-editor__body {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-4);
  padding: var(--st-space-3);
  overflow: auto;
}
.category-editor__hint {
  font-size: var(--st-text-sm);
  color: var(--st-text-muted);
  max-width: 40rem;
  line-height: 1.5;
}
.category-editor__section {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-2);
}
.category-editor__sub {
  font-size: var(--st-text-sm);
  color: var(--st-text-muted);
  max-width: 40rem;
  line-height: 1.5;
}
</style>
